import { useCore } from '@/composables/core'
import { getAnalyticsToken, getAppsflyerUserId, getGoogleAnalyticsClientId } from '@/lib/analytics/helpers'
import { getPublicApiCallUrl } from '@/api/get-public-api-call-url'
import { isLocalhost, useLogger, useUuid, createBooksyApiClient, useBooksyApiClient } from '@booksy-com/vue-tools'
import { computed } from 'vue'
import axios from '@/api/axios'

const logger = useLogger('api-client')

type ApiCallConfig = Record<string, unknown> & {
  url: string
  apiVersion?: string
}

export const createApiClient = () => {
  const { store } = useCore()
  const country = computed(() => store.state.app.country)
  const fingerprint = computed(() => store.state.app.fingerprint)
  const language = computed(() => store.getters['app/acceptLanguage'])
  const accessToken = computed(() => store.state.identity.accessToken)
  const businessId = computed(() => store.state.business.business.id)
  const uuid = useUuid()

  const config = {
    domain: window.location.origin,
    proxy: process.env.VUE_APP_PROXY_LOCALHOST
      || isLocalhost(window.location.origin),
    key: store.state.api.apiKey,
    axios,
    country,
    language,
    fingerprint,
    accessToken,
    appVersion: '3.0',
    bksReqId: () => uuid.v4(),
    userPseudoId: () => getGoogleAnalyticsClientId(),
    appsflyerUserId: () => getAppsflyerUserId(),
    analyticsTokens: () => getAnalyticsToken(),
    urlParams: {
      businessId,
    },
  }

  axios.interceptors.request.use(async (requestConfig) => {
    const { store } = useCore()
    const cfg = await store.dispatch('api/addHCaptcha', requestConfig)
    return cfg
  })

  return createBooksyApiClient(config as Parameters<typeof createBooksyApiClient>[0])
}

export function useApiClient () {
  return useBooksyApiClient()
}

export const transformConfig = (config: ApiCallConfig): ApiCallConfig => {
  const apiClient = useApiClient()
  const { store } = useCore()
  const publicApiUrl = store.state.api.publicApiUrl

  const cfg = { ...config }

  const apiVersion = config.apiVersion || store.state.api.apiVersion

  cfg.url = getPublicApiCallUrl(cfg.url, publicApiUrl)

  cfg.url = cfg.url.replace(/:([a-z0-9]+)(\/|$)/gi, '{$1}$2')

  if (!apiClient.detectService(cfg.url)) {
    cfg.url = `/core/v${apiVersion}${cfg.url}`
  }

  logger.debug('API call url transformed', config.url, '>>', cfg.url)

  return cfg
}
